import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useQuery, type QueryFunctionContext } from '@tanstack/react-query'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { getCartPaymentStatus, type CartPaymentStatusRequest } from './cart-payments.api'

interface OrganizationScopeInput {
  organizationId?: OrganizationIdInput
}

type StatusQueryInput = OrganizationScopeInput & Omit<CartPaymentStatusRequest, 'organizationId'>
type StatusQueryKey = [AdminQueryRoot & CartPaymentStatusRequest & { organizationId: number }]
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

function runCartPaymentStatusQuery({
  queryKey: [{ organizationId, cartId, paymentId }],
}: Pick<QueryFunctionContext<StatusQueryKey>, 'queryKey'>) {
  return getCartPaymentStatus({ organizationId, cartId, paymentId })
}

export const cartPaymentQueryKeys = {
  all: ({ organizationId }: OrganizationScopeInput = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'cart-payment' }, { organizationId }),
  ],
  status: ({ organizationId, cartId, paymentId }: StatusQueryInput = {}): StatusQueryKey =>
    buildScopedAdminQueryKey(cartPaymentQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'status',
      cartId,
      paymentId,
    }),
}

export const cartPaymentQueries = {
  status: ({ organizationId, cartId, paymentId }: StatusQueryInput = {}) =>
    queryOptions({
      queryKey: cartPaymentQueryKeys.status({ organizationId, cartId, paymentId }),
      queryFn: runCartPaymentStatusQuery,
      enabled: Boolean(cartId && paymentId),
      staleTime: 0,
    }),
}

export function useCartPaymentStatus(
  cartId: CartPaymentStatusRequest['cartId'],
  paymentId: CartPaymentStatusRequest['paymentId'],
  options: QueryParams<ReturnType<typeof cartPaymentQueries.status>> = {}
) {
  const organizationId = useRouteOrganizationId()

  return useQuery({
    ...cartPaymentQueries.status({ organizationId, cartId, paymentId }),
    ...options,
  })
}
